import { useToggle } from "@artists/devkit"
import type { VotesToMerge } from "@artists/gql/design-challenges"
import { CustomErrorResponses } from "@artists/gql/design-challenges"
import { Typography } from "@mui/material"
import type { FC, RefObject } from "react"
import { useTranslation } from "react-i18next"

import type { Challenge, EntryWithVote } from "../../../api/hooks/vote"
import { Controls } from "./controls"
import { VotingList } from "./listing"
import { MergeVotesPrompt } from "./merge-votes-prompt"
import { useOffset } from "./offset-context"
import { PromotedEntry } from "./promoted-entry"
import { FraudPrompt, LastDayPrompt, VotingEndedPrompt } from "./warnings"

export interface VoteProps {
	challenge: Maybe<Challenge>
	guestVoting: Maybe<VotesToMerge>
	errors: string[]
	entriesRef: RefObject<HTMLDivElement>
	isDataReceived: boolean
	isAlreadyVoted: boolean
	loading: boolean
	promotion: Maybe<EntryWithVote>
	entriesForVoting: EntryWithVote[] | undefined
}
export const Vote: FC<VoteProps> = ({
	errors,
	challenge,
	guestVoting,
	entriesForVoting = [],
	promotion,
	entriesRef,
	isAlreadyVoted,
	loading,
}) => {
	const { t } = useTranslation("vote")
	const [isOpen, open, close] = useToggle(false)
	const { currentPage, totalPages } = useOffset()

	return (
		<>
			{promotion && challenge && (
				<PromotedEntry promotion={promotion} slug={challenge.slug} />
			)}
			{isAlreadyVoted && (
				<Typography
					mb={3}
					color='warning.dark'
					fontWeight='bold'
					component='p'
					textAlign='center'
				>
					{t("continueVote", {
						current: currentPage,
						total: totalPages,
					})}
				</Typography>
			)}
			<VotingList
				items={entriesForVoting}
				challenge={challenge}
				loading={loading}
			/>
			{challenge && entriesForVoting && (
				<Controls
					challenge={challenge}
					loading={loading}
					entriesContainer={entriesRef}
					onVotingEnded={open}
				/>
			)}
			{challenge && (
				<>
					<LastDayPrompt votingClosesAt={challenge.votingClosesAt} />
					<FraudPrompt
						withIpError={errors.includes(
							CustomErrorResponses.IpError,
						)}
						slug={challenge.slug}
					/>
					<VotingEndedPrompt
						isOpen={isOpen}
						onClose={close}
						votingClosesAt={challenge.votingClosesAt}
						slug={challenge.slug}
					/>
					{guestVoting && (
						<MergeVotesPrompt
							guestVoting={guestVoting}
							slug={challenge.slug}
						/>
					)}
				</>
			)}
		</>
	)
}
